import { KpiSettings, ObjectiveType } from '@mm-shared/types/Objective1';
import { ContinuousCode } from '@mm-shared/types/Status1';

/**
 * Calculate the continuous status code for a given value based on KPI settings
 * Maps a numerical value to one of 22 continuous codes (r1-r6, y7-y12, g13-g22)
 *
 * TODO: Determine if this should be replaced with d3.
 *
 * @param value - The metric value to evaluate
 * @param settings - KPI settings defining thresholds and direction
 * @returns ContinuousCode representing the performance level
 */
export function calculateContinuousCode(value: number, settings: KpiSettings): ContinuousCode {
  const { ranges, higherIsBetter } = settings;
  const { bestGreen, bestYellow, worstYellow, worstRed } = ranges;

  // Determine value ranges based on direction
  let greenStart: number, greenEnd: number;
  let yellowStart: number, yellowEnd: number;
  let redStart: number, redEnd: number;

  // "start" is always the lower value and "end" is always the higher value.
  if (higherIsBetter) {
    // Higher is better: bestGreen is highest, worstRed is lowest
    greenStart = bestYellow;
    greenEnd = bestGreen;
    yellowStart = worstYellow;
    yellowEnd = bestYellow;
    redStart = worstRed;
    redEnd = worstYellow;
  } else {
    // Lower is better: bestGreen is lowest, worstRed is highest
    greenStart = bestGreen;
    greenEnd = bestYellow;
    yellowStart = bestYellow;
    yellowEnd = worstYellow;
    redStart = worstYellow;
    redEnd = worstRed;
  }

  // Helper function to map value to a code within a range
  function mapToCode(
    val: number,
    start: number,
    end: number,
    codes: ContinuousCode[],
  ): ContinuousCode {
    const range = Math.abs(end - start);
    if (range === 0) return codes[0];

    const position = Math.abs(val - start) / range;
    const index = Math.min(Math.floor(position * codes.length), codes.length - 1);
    return codes[index];
  }

  // Green spectrum codes (10 levels)
  const greenCodes: ContinuousCode[] = [
    'g13',
    'g14',
    'g15',
    'g16',
    'g17',
    'g18',
    'g19',
    'g20',
    'g21',
    'g22',
  ];

  // Yellow spectrum codes (6 levels)
  const yellowCodes: ContinuousCode[] = ['y7', 'y8', 'y9', 'y10', 'y11', 'y12'];

  // Red spectrum codes (6 levels)
  const redCodes: ContinuousCode[] = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6'];

  // Determine which spectrum the value falls into
  if (higherIsBetter) {
    if (value > bestYellow) {
      // Green zone
      return mapToCode(value, greenStart, greenEnd, greenCodes);
    } else if (value >= worstYellow) {
      // Yellow zone
      return mapToCode(value, yellowStart, yellowEnd, yellowCodes);
    } else {
      // Red zone
      return mapToCode(value, redStart, redEnd, redCodes);
    }
  } else {
    // Lower is better
    if (value < bestYellow) {
      // Green zone
      return mapToCode(value, greenStart, greenEnd, greenCodes);
    } else if (value <= worstYellow) {
      // Yellow zone
      return mapToCode(value, yellowStart, yellowEnd, yellowCodes);
    } else {
      // Red zone
      return mapToCode(value, redStart, redEnd, redCodes);
    }
  }
}

/**
 * Validate KPI settings to prevent illogical configurations
 *
 * @param settings - KPI settings to validate
 * @returns Object with validation result and array of error messages
 */
export function validateKpiSettings(settings: KpiSettings): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const { ranges, higherIsBetter, numberType } = settings;
  const { bestGreen, bestYellow, worstYellow, worstRed } = ranges;

  // Check for valid number type
  if (numberType !== '%' && numberType !== '#') {
    errors.push(`Invalid numberType: ${numberType}. Must be '%' or '#'`);
  }

  // Validate percentage constraints
  if (numberType === '%') {
    const values = [bestGreen, bestYellow, worstYellow, worstRed];
    values.forEach((val, idx) => {
      const names = ['bestGreen', 'bestYellow', 'worstYellow', 'worstRed'];
      if (val < 0 || val > 100) {
        errors.push(`${names[idx]} must be between 0 and 100 for percentage type (got ${val})`);
      }
    });
  }

  // Validate threshold ordering based on direction
  if (higherIsBetter) {
    // Higher is better: bestGreen > bestYellow > worstYellow > worstRed
    if (bestGreen <= bestYellow) {
      errors.push(
        `When higherIsBetter=true, bestGreen (${bestGreen}) must be > bestYellow (${bestYellow})`,
      );
    }
    if (bestYellow <= worstYellow) {
      errors.push(
        `When higherIsBetter=true, bestYellow (${bestYellow}) must be > worstYellow (${worstYellow})`,
      );
    }
    if (worstYellow <= worstRed) {
      errors.push(
        `When higherIsBetter=true, worstYellow (${worstYellow}) must be > worstRed (${worstRed})`,
      );
    }
  } else {
    // Lower is better: bestGreen < bestYellow < worstYellow < worstRed
    if (bestGreen >= bestYellow) {
      errors.push(
        `When higherIsBetter=false, bestGreen (${bestGreen}) must be < bestYellow (${bestYellow})`,
      );
    }
    if (bestYellow >= worstYellow) {
      errors.push(
        `When higherIsBetter=false, bestYellow (${bestYellow}) must be < worstYellow (${worstYellow})`,
      );
    }
    if (worstYellow >= worstRed) {
      errors.push(
        `When higherIsBetter=false, worstYellow (${worstYellow}) must be < worstRed (${worstRed})`,
      );
    }
  }

  // Check for NaN or invalid numbers
  const allValues = [bestGreen, bestYellow, worstYellow, worstRed];
  allValues.forEach((val, idx) => {
    const names = ['bestGreen', 'bestYellow', 'worstYellow', 'worstRed'];
    if (isNaN(val) || !isFinite(val)) {
      errors.push(`${names[idx]} must be a valid number (got ${val})`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get the default objective type
 * Provides backwards compatibility by defaulting to 'deliverable'
 *
 * @param type - The objective type or undefined
 * @returns The objective type, defaulting to 'deliverable' if not specified
 */
export function getObjectiveType(type: ObjectiveType | undefined): ObjectiveType {
  return type ?? 'deliverable';
}

/**
 * Check if an objective is metric-oriented
 *
 * @param type - The objective type or undefined
 * @returns True if the objective is metric type
 */
export function isMetricObjective(type: ObjectiveType | undefined): boolean {
  return type === 'metric';
}

/**
 * Check if an objective is deliverable-oriented
 *
 * @param type - The objective type or undefined
 * @returns True if the objective is deliverable type (or undefined for backwards compatibility)
 */
export function isDeliverableObjective(type: ObjectiveType | undefined): boolean {
  return type === 'deliverable';
}

/**
 * Generate default KPI settings for common metric types
 *
 * @param numberType - The number type (percentage or real number)
 * @param higherIsBetter - Whether higher values are better
 * @returns Default KPI settings
 */
export function getDefaultKpiSettings(numberType: '%' | '#', higherIsBetter: boolean): KpiSettings {
  if (numberType === '%') {
    return higherIsBetter
      ? {
          numberType: '%',
          higherIsBetter: true,
          ranges: {
            bestGreen: 100,
            bestYellow: 89,
            worstYellow: 70,
            worstRed: 0,
          },
        }
      : {
          numberType: '%',
          higherIsBetter: false,
          ranges: {
            bestGreen: 0,
            bestYellow: 11,
            worstYellow: 30,
            worstRed: 100,
          },
        };
  } else {
    // Real number defaults (need to be customized per metric)
    return higherIsBetter
      ? {
          numberType: '#',
          higherIsBetter: true,
          ranges: {
            bestGreen: 10000,
            bestYellow: 8900,
            worstYellow: 7000,
            worstRed: 5000,
          },
        }
      : {
          numberType: '#',
          higherIsBetter: false,
          ranges: {
            bestGreen: 0,
            bestYellow: 99,
            worstYellow: 200,
            worstRed: 300,
          },
        };
  }
}
